/**
 * ImageCropper — Modal cropper for negocio uploads.
 *
 * Wraps react-image-crop with Instagram presets (quadrado 1:1, paisagem 1.91:1,
 * retrato 4:5, stories 9:16, livre).
 *
 * Props:
 *   open: bool — modal visibility
 *   file: File — original file from input
 *   defaultFormato: 'quadrado'|'paisagem'|'retrato'|'stories'|'livre'
 *   onConfirm({ blob, formato, crop })  — crop applied; returns canvas blob (jpeg)
 *   onCancel()
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, Check, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const FORMATOS = [
  { id: 'quadrado', label: 'Quadrado 1:1', aspect: 1 },
  { id: 'paisagem', label: 'Paisagem 1.91:1', aspect: 1.91 },
  { id: 'retrato', label: 'Retrato 4:5', aspect: 4 / 5 },
  { id: 'stories', label: 'Stories 9:16', aspect: 9 / 16 },
  { id: 'livre', label: 'Livre', aspect: undefined },
];

function buildCenteredCrop(width, height, aspect) {
  if (!aspect) {
    return { unit: '%', x: 5, y: 5, width: 90, height: 90 };
  }
  return centerCrop(
    makeAspectCrop(
      { unit: '%', width: 80 },
      aspect,
      width,
      height
    ),
    width,
    height
  );
}

export default function ImageCropper({
  open,
  file,
  defaultFormato = 'quadrado',
  onConfirm,
  onCancel,
}) {
  const [imgSrc, setImgSrc] = useState('');
  const [formato, setFormato] = useState(defaultFormato);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);

  // Read file as data URL
  useEffect(() => {
    if (!file) {
      setImgSrc('');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setImgSrc(e.target.result);
    reader.readAsDataURL(file);
  }, [file]);

  // Reset formato when modal reopens
  useEffect(() => {
    if (open) setFormato(defaultFormato);
  }, [open, defaultFormato]);

  const aspect = FORMATOS.find((f) => f.id === formato)?.aspect;

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    setCrop(buildCenteredCrop(width, height, aspect));
  }, [aspect]);

  // Re-center crop when aspect changes
  const handleFormatoChange = (nextId) => {
    setFormato(nextId);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      const nextAspect = FORMATOS.find((f) => f.id === nextId)?.aspect;
      setCrop(buildCenteredCrop(width, height, nextAspect));
    }
  };

  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop) return;
    setBusy(true);
    try {
      const img = imgRef.current;
      // Crop returns coords in displayed-image pixels; scale to natural pixels.
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      const cropX = Math.round(completedCrop.x * scaleX);
      const cropY = Math.round(completedCrop.y * scaleY);
      const cropW = Math.round(completedCrop.width * scaleX);
      const cropH = Math.round(completedCrop.height * scaleY);

      const canvas = document.createElement('canvas');
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

      // Export as JPEG (smaller for upload). Backend re-encodes to webp anyway.
      const blob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
      });

      onConfirm?.({
        blob,
        formato,
        crop: { x: cropX, y: cropY, width: cropW, height: cropH },
      });
    } finally {
      setBusy(false);
    }
  };

  const handleResetCrop = () => {
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(buildCenteredCrop(width, height, aspect));
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
      data-testid="image-cropper-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="font-bold text-slate-900">Ajustar imagem</h3>
            <p className="text-xs text-slate-500">Selecione um formato e ajuste o enquadramento.</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg"
            data-testid="cropper-cancel-btn"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Formato selector */}
        <div className="flex gap-2 px-5 py-3 border-b border-slate-200 overflow-x-auto">
          {FORMATOS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => handleFormatoChange(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                formato === f.id
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid={`cropper-formato-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Crop area */}
        <div className="flex-1 overflow-auto bg-slate-900 flex items-center justify-center p-4 min-h-[300px]">
          {imgSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              className="max-h-[60vh]"
              ruleOfThirds
            >
              <img
                ref={imgRef}
                src={imgSrc}
                alt="Original"
                onLoad={onImageLoad}
                className="max-h-[60vh] max-w-full"
              />
            </ReactCrop>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Badge variant="outline">{FORMATOS.find((f) => f.id === formato)?.label}</Badge>
            {completedCrop && (
              <span className="tabular-nums text-slate-500">
                {Math.round(completedCrop.width * (imgRef.current?.naturalWidth / (imgRef.current?.width || 1)))}
                ×
                {Math.round(completedCrop.height * (imgRef.current?.naturalHeight / (imgRef.current?.height || 1)))}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleResetCrop} data-testid="cropper-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Centralizar
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={busy || !completedCrop}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="cropper-confirm-btn"
            >
              <Check className="w-3.5 h-3.5 mr-1" /> Aplicar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
